import React, { useCallback, useContext, useEffect, useRef } from 'react'

import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'

import { pushWithRegion } from 'actions/NavigationActions'

import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'

import GlobalSearchTrackingContext from 'contexts/GlobalSearch/GlobalSearchTracking'
import { OFFER_TYPE_EXPERIENCE } from 'constants/offer'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import { trackClientEvent, trackEvent } from 'analytics/analytics'
import { searchEventWithContext } from 'analytics/snowplow/events'
import createSearchContext from 'analytics/snowplow/helpers/createSearchContext'
import { mapGlobalSearchContextToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import ExperiencesSearchInputs from './ExperiencesSearchInputs'
import { type QueryStringSearchParams, replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import uuidV4 from 'lib/string/uuidV4Utils'

interface Props {
  onSearch?: (searchItem?: App.SearchItem) => void
  isMapView?: boolean
  className?: string
}

function ExperiencesSearchControls(props: Props) {
  const {
    onSearch,
    isMapView = false,
    className,
  } = props
  const onSearchEvent = useContext(GlobalSearchTrackingContext)

  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()

  const { searchItem, checkinDate, checkoutDate, dateSearchOptionId, experienceCategories, suggestedSearchItems } = useContext(GlobalSearchStateContext)

  const toBeSearchedItem = searchItem ?? suggestedSearchItems[0] ?? ANYWHERE_SEARCH_ITEM
  const fetchingExperience = useAppSelector(state => state.experience.fetchingExperiences[toBeSearchedItem.value])
  const searchSessionId = useRef(uuidV4())
  const searchPhrase = useRef('')
  const searchPhraseTrackingTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const getSearchContext = useCallback((naturalLanguageQuery?: string, selectedSearchItem?: App.SearchItem, effectiveCheckoutDate = checkoutDate ?? checkinDate) => ({
    ...mapGlobalSearchContextToSnowplowSearchEvent({
      searchItem: selectedSearchItem ?? searchItem,
      checkinDate,
      checkoutDate: effectiveCheckoutDate,
      dateSearchOptionId,
      searchVerticals: new Set(['experiences']),
    }),
    naturalLanguageQuery,
  }), [searchItem, checkinDate, checkoutDate, dateSearchOptionId])

  const trackSearchPhrase = useCallback((naturalLanguageQuery: string) => {
    trackClientEvent({
      subject: 'experiences_search_bar',
      action: 'input_changed',
      category: 'search',
      type: 'interaction',
      context: [
        createSearchContext(getSearchContext(naturalLanguageQuery)),
        comluxgroup.createSearchSession_1_0_0({ searchSessionId: searchSessionId.current }),
      ],
    })
  }, [getSearchContext])

  const handleSearchPhraseChange = useCallback((nextSearchPhrase: string) => {
    searchPhrase.current = nextSearchPhrase

    if (searchPhraseTrackingTimeout.current) {
      clearTimeout(searchPhraseTrackingTimeout.current)
    }

    searchPhraseTrackingTimeout.current = setTimeout(() => {
      trackSearchPhrase(nextSearchPhrase)
      searchPhraseTrackingTimeout.current = undefined
    }, 500)
  }, [trackSearchPhrase])

  useEffect(() => () => {
    if (searchPhraseTrackingTimeout.current) {
      clearTimeout(searchPhraseTrackingTimeout.current)
    }
  }, [])

  const handleSearchButtonClick = useCallback((e?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>, selectedSearchItem?: App.SearchItem) => {
    e?.preventDefault()

    if (searchPhraseTrackingTimeout.current) {
      clearTimeout(searchPhraseTrackingTimeout.current)
      trackSearchPhrase(searchPhrase.current)
      searchPhraseTrackingTimeout.current = undefined
    }

    const item = selectedSearchItem ?? toBeSearchedItem
    const formattedCheckinDate = checkinDate?.format(ISO_DATE_FORMAT)
    const effectiveCheckout = checkoutDate ?? checkinDate
    const formattedCheckoutDate = effectiveCheckout?.format(ISO_DATE_FORMAT)

    const query: QueryStringSearchParams = {
      checkIn: formattedCheckinDate,
      checkOut: formattedCheckoutDate,
    }

    if (experienceCategories?.length) {
      query.categories = experienceCategories.join(',')
    }

    const luxPlusFeatures = queryParams.get('luxPlusFeatures')

    if (luxPlusFeatures) {
      query.luxPlusFeatures = luxPlusFeatures
    }

    if (item.searchType === 'experience') {
      query.experienceId = item.value
      query.experienceName = item.format.mainText
    } else {
      query.destinationId = item.value
      query.destinationName = item.format.mainText
    }

    const queryString = replaceExperienceQueryString(query)

    trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      searchContext: getSearchContext(searchPhrase.current || undefined, selectedSearchItem, effectiveCheckout),
      searchSessionId: searchSessionId.current,
    }))

    searchSessionId.current = uuidV4()
    searchPhrase.current = ''

    const path = isMapView ? '/search/experiences/map' : '/search/experiences'

    dispatch(pushWithRegion(`${path}?${queryString}`))

    onSearchEvent('submit-button', {
      searchItem: selectedSearchItem ?? searchItem,
      vertical: OFFER_TYPE_EXPERIENCE,
      checkIn: formattedCheckinDate,
      checkOut: formattedCheckoutDate,
    })

    onSearch?.(selectedSearchItem ?? searchItem)
  }, [onSearch, searchItem, checkinDate, checkoutDate, experienceCategories, isMapView, dispatch, onSearchEvent, toBeSearchedItem, queryParams, getSearchContext, trackSearchPhrase])

  return <div className={className}>
    <ExperiencesSearchInputs
      onSearchButtonClick={handleSearchButtonClick}
      onSearchPhraseChange={handleSearchPhraseChange}
      disableSearch={fetchingExperience}
    />
  </div>
}

export default ExperiencesSearchControls
